// RBAC-aware in-app help content for TPA InsureOS.
//
// Sections are tagged with the business roles they apply to. The drawer shows a
// section only when the signed-in user's effective role matches (the platform
// 'admin' superuser sees everything), plus the universal Welcome / FAQ sections
// and a dynamically-built "Your workspace" section listing the pages the user
// can actually open. Icons are FontAwesome CSS classes (the app uses <i className>).

import { ROLES, isSuperuser, effectiveRoles, canAccess } from '../../config/roles.js';
import { menuData } from '../../config/menuData.js';

const R = ROLES;

// ---- Universal sections (every role) ----
const WELCOME = {
  id: 'welcome',
  title: 'Welcome',
  icon: 'fa fa-rocket',
  roles: 'all',
  topics: [
    {
      id: 'overview',
      title: 'Overview',
      body: `
        <p><strong>TPA InsureOS</strong> is a Third-Party Administration platform that runs medical, dental, and veterinary benefits end to end — eligibility and pre-authorization, claims intake and adjudication, finance and payouts, and compliance reporting.</p>
        <p style="margin-top:8px;">What you see is scoped to your <strong>role</strong>. Each role gets its own workspace and navigation — a member sees self-service pages, a claims adjudicator sees the adjudication queue, an administrator sees user and integration management, and so on. You will only ever see the pages your role is permitted to use.</p>
        <p style="margin-top:8px; font-size:13px; color:#9ca3af;">Tip: open the <strong>Your workspace</strong> tab here to see exactly which pages your account can open and where you land after signing in.</p>`,
    },
    {
      id: 'navigation',
      title: 'Navigation & roles',
      body: `
        <p>Your pages live in the top navigation bar on desktop. Roles with only a few pages show them as direct links; roles with many pages group them under dropdown menus.</p>
        <p style="margin-top:12px;"><strong>On a phone:</strong></p>
        <p>The top bar is replaced by a <strong>bottom tab bar</strong> with your most-used pages plus a <strong>More</strong> button that opens the full menu — the app behaves like a native mobile app. On desktop the navigation returns to the top.</p>
        <p style="margin-top:12px;"><strong>Account menu:</strong></p>
        <p>Your name and avatar sit at the top-right. Open it to see your email and to <strong>Log out</strong>. The <strong>?</strong> button next to it opens this help at any time.</p>`,
    },
    {
      id: 'install',
      title: 'Install as an app',
      body: `
        <p>InsureOS is a Progressive Web App (PWA) — you can install it to your home screen or desktop and launch it like a native app, with offline-friendly loading.</p>
        <ol>
          <li><strong>Desktop (Chrome/Edge)</strong> — click the <strong>Install</strong> icon in the address bar, or browser menu &rarr; <em>Install TPA InsureOS</em>.</li>
          <li style="margin-top:8px;"><strong>iPhone (Safari)</strong> — tap <strong>Share</strong> &rarr; <em>Add to Home Screen</em>.</li>
          <li style="margin-top:8px;"><strong>Android (Chrome)</strong> — tap the menu &rarr; <em>Install app</em> / <em>Add to Home screen</em>.</li>
        </ol>
        <p style="margin-top:8px; font-size:13px; color:#9ca3af;">Once installed it opens full-screen with the bottom tab bar, just like a mobile app.</p>`,
    },
  ],
};

const FAQ = {
  id: 'faq',
  title: 'FAQ',
  icon: 'fa fa-circle-question',
  roles: 'all',
  topics: [
    {
      id: 'access',
      title: 'Access & roles',
      body: `
        <p><strong>Why can't I see a page a colleague has?</strong> Pages are gated by role. You see only what your role permits; if you reach a page you can't access you'll see a <span style="color:#ef4444;">403</span> screen with a <strong>Go to Dashboard</strong> button that returns you to your home page.</p>
        <p style="margin-top:8px;"><strong>I need a different role or another page.</strong> Ask your System Administrator — they manage accounts and role assignments under <strong>User Management</strong>.</p>
        <p style="margin-top:8px;"><strong>Demo accounts.</strong> On the sign-in screen, the <em>Demo Accounts — Dev Only</em> list lets you one-click in as any role to explore. These are for non-production environments.</p>`,
    },
    {
      id: 'security',
      title: 'Security & sign-in',
      body: `
        <p>Sessions use signed JWT tokens. If your session expires you'll be returned to the sign-in screen and, after signing back in, taken to your role's home (not to a page you can't access).</p>
        <p style="margin-top:8px;"><strong>Multi-factor authentication (MFA).</strong> Where enforced, you'll be prompted to enroll. An administrator can reset your MFA enrollment from User Management if you change devices.</p>
        <p style="margin-top:8px;"><strong>Forgot password.</strong> Use the link on the sign-in screen, or ask an administrator to issue a temporary password.</p>`,
    },
    {
      id: 'data',
      title: 'Statutory rules (PH)',
      body: `
        <p>InsureOS applies Philippine statutory benefit rules during adjudication where enabled on the plan:</p>
        <ul>
          <li><strong>PhilHealth-first</strong> — statutory case-rate benefits are deducted before plan limits.</li>
          <li><strong>Senior Citizen / PWD</strong> — 20% discount and 12% VAT exemption on qualified professional fees.</li>
          <li><strong>Withholding tax</strong> — 10% or 15% professional WHT on provider payments depending on BIR status.</li>
        </ul>
        <p style="margin-top:8px; font-size:13px; color:#9ca3af;">These are configured by Operations on each plan and applied automatically by the adjudication engine.</p>`,
    },
  ],
};

// ---- Role-grouped sections ----
const SECTIONS = [
  {
    id: 'member',
    title: 'Member',
    icon: 'fa fa-user',
    roles: [R.MEMBER],
    topics: [
      {
        id: 'member-loa',
        title: 'Request an LOA',
        body: `
          <p>Use <strong>Request Outpatient LOA</strong> to get a cashless Letter of Authorization before a checkup.</p>
          <ol>
            <li>Choose your policy and search for an <strong>accredited clinic and doctor</strong>.</li>
            <li>Select the complaint, list the requested consults/tests, and upload your referral.</li>
            <li>Submit — if within limits it auto-approves or routes to a Medical Review Officer.</li>
          </ol>
          <p style="margin-top:8px;">Once approved you can download the cashless LOA PDF and present it at the clinic.</p>`,
      },
      {
        id: 'member-reimburse',
        title: 'File a reimbursement',
        body: `
          <p>Paid out of pocket? Use <strong>File Reimbursement Claim</strong>.</p>
          <p style="margin-top:8px;">Enter the treatment date, facility, and amount paid, choose your bank or e-wallet (GCash, Maya, BPI, BDO) for PHP disbursement, and upload your <strong>Official Receipt</strong>, <strong>Medical Certificate</strong>, and <strong>itemized billing</strong>. You'll get a tracking ID and can follow it through review and payout.</p>`,
      },
      {
        id: 'member-eob',
        title: 'Read your EOB',
        body: `
          <p><strong>Benefits &amp; Settlement Breakdown</strong> shows every processed claim with its outcome — <span style="color:#22c55e;">Approved</span>, <span style="color:#f59e0b;">Adjusted</span>, or <span style="color:#ef4444;">Rejected</span> — and a line-by-line explanation of benefits: PhilHealth and discount offsets, deductions, your final responsibility, and limit consumption. If you disagree, you can initiate an <strong>appeal</strong> from the claim.</p>`,
      },
    ],
  },
  {
    id: 'provider',
    title: 'Providers',
    icon: 'fa fa-hospital',
    roles: [R.HEALTHCARE_PROVIDER, R.DENTAL_PROVIDER, R.VETERINARY_CLINIC, R.PROVIDER_RELATIONS_OFFICER],
    topics: [
      {
        id: 'provider-eligibility',
        title: 'Check eligibility & roster',
        body: `
          <p>From the <strong>Provider Console</strong> you can verify a member's coverage in real time, update your clinic's contact details and operating hours, and manage your <strong>accredited doctors</strong> (each with their BIR withholding-tax status). Roster changes are submitted to TPA Operations for approval.</p>`,
      },
      {
        id: 'provider-submit',
        title: 'Submit LOA / claims',
        body: `
          <p>Healthcare providers submit walk-in cashless <strong>LOA requests</strong>, <strong>direct-billing claims</strong> against an approved LOA, and inpatient <strong>GOP requests</strong> and <strong>hospital billing</strong>. Enter ICD-10 codes and itemized charges and upload the supporting documents; each submission is queued for TPA review with a tracking reference.</p>`,
      },
      {
        id: 'provider-preauth',
        title: 'Dental / veterinary pre-auth',
        body: `
          <p>Dental providers use <strong>Submit Dental Pre-Auth</strong> with an interactive tooth chart — select teeth, surfaces, and procedures, add a diagnostic remark, and upload the panoramic X-ray.</p>
          <p style="margin-top:8px;">Veterinary clinics use <strong>Submit Veterinary Pre-Auth</strong> — verify the pet policy (coverage % and remaining limit), choose the diagnosis and procedures, and upload clinical notes. Both route to the relevant review officer.</p>`,
      },
    ],
  },
  {
    id: 'corporate',
    title: 'Corporate HR',
    icon: 'fa fa-building',
    roles: [R.CORPORATE_CLIENT],
    topics: [
      {
        id: 'corp-roster',
        title: 'Manage the roster',
        body: `
          <p>The <strong>HR Roster Dashboard</strong> lists your employees and dependents with plan tier and coverage status. Filter by tier or status, and use the row actions to <strong>Approve</strong> pending enrollments, <strong>Terminate</strong> leavers (sets termination date and suspends coverage), or <strong>Rehire</strong>.</p>`,
      },
      {
        id: 'corp-enroll',
        title: 'Enroll employees',
        body: `
          <p>Add people two ways under <strong>Enroll Individual Employee</strong>:</p>
          <ul>
            <li><strong>One at a time</strong> — fill the form (employee ID, name, email, plan tier, dates) and optionally register dependents.</li>
            <li><strong>Bulk (Excel/CSV)</strong> — click <strong>Download Template</strong>, fill it in Excel, save as <code>.csv</code>, and upload. Rows are validated and previewed (invalid rows flagged) before anything is submitted.</li>
          </ul>
          <p style="margin-top:8px;">New enrollments enter the roster as <span style="color:#f59e0b;">Pending Approval</span>.</p>`,
      },
    ],
  },
  {
    id: 'claims',
    title: 'Claims Ops',
    icon: 'fa fa-briefcase-medical',
    roles: [R.CLAIMS_PROCESSOR, R.CLAIMS_ADJUDICATOR, R.FRAUD_ANALYST],
    topics: [
      {
        id: 'claims-intake',
        title: 'Intake & coordination',
        body: `
          <p>Claims Processors index incoming claims in the <strong>Auditing Inbox</strong>, confirm documents and eligibility, and route them for adjudication (or back with a deficiency checklist). <strong>OCR Intake</strong> extracts key fields from uploaded documents for review, and <strong>Coordination of Benefits</strong> sequences PhilHealth and other payers to compute residual member liability.</p>`,
      },
      {
        id: 'claims-adjudicate',
        title: 'Adjudicate & appeal',
        body: `
          <p>In the <strong>Adjudication Workspace</strong>, review the clinical files and itemized charges. The engine applies PhilHealth case rates, Senior/PWD discounts, VAT exemption, co-pay, and the plan limit, then you <strong>Approve</strong>, <strong>Adjust</strong> (with reason codes), or <strong>Reject</strong>. Post-decision disputes are handled in <strong>Appeals &amp; Grievances</strong>.</p>`,
      },
      {
        id: 'claims-fwa',
        title: 'Fraud, waste & abuse',
        body: `
          <p>Fraud Analysts review flagged duplicates and anomalies in <strong>FWA &amp; Duplicate Detection</strong>. Filter by provider, member, or detection category, inspect linked claims, and route suspicious cases for investigation while preserving the flag details and audit history.</p>`,
      },
    ],
  },
  {
    id: 'clinical',
    title: 'Clinical Review',
    icon: 'fa fa-user-md',
    roles: [R.MEDICAL_REVIEW_OFFICER, R.DENTAL_REVIEW_OFFICER, R.VET_REVIEW_OFFICER],
    topics: [
      {
        id: 'clinical-queue',
        title: 'Pre-auth review',
        body: `
          <p>The <strong>Clinical Pre-Auth Queue</strong> holds pending LOA and pre-authorization requests. Review diagnostic codes, documents, and medical necessity, optionally consult <strong>Clinical Guidelines</strong>, then approve (issues a digital LOA code), request more records, or reject with a reason. Dental and veterinary reviewers see their specialty's requests with tooth-chart / pet-policy context.</p>`,
      },
      {
        id: 'clinical-inpatient',
        title: 'Inpatient authorization',
        body: `
          <p>Medical Review Officers assess inpatient admission and continued-stay requests in <strong>Inpatient Stay Authorization Review</strong> — checking medical necessity, benefit coverage, length of stay, and bed-day utilization — and approve, modify, defer, extend, or deny coverage with rationale recorded to the case trail.</p>`,
      },
    ],
  },
  {
    id: 'finance',
    title: 'Finance',
    icon: 'fa fa-university',
    roles: [R.FINANCE_USER],
    topics: [
      {
        id: 'finance-payments',
        title: 'Disbursements & WHT',
        body: `
          <p>In the <strong>Finance &amp; Disbursements</strong> panel, review approved payments and process provider payouts. The system computes professional <strong>withholding tax</strong> (10% or 15% by BIR status), produces the net payout statement, disburses via your banking/e-wallet partner, and exports the daily PESONet/InstaPay ledger.</p>`,
      },
      {
        id: 'finance-reconcile',
        title: 'Payouts & reconciliation',
        body: `
          <p>Execute member <strong>reimbursement payouts</strong>, manage <strong>bank / e-wallet integrations</strong>, and reconcile carrier or employer funding against claims paid and billings in <strong>Carrier Funds &amp; Billing Reconciliation</strong> — flag variances, attach evidence, and close or escalate gaps.</p>`,
      },
    ],
  },
  {
    id: 'operations',
    title: 'Operations',
    icon: 'fa fa-cogs',
    roles: [R.OPERATIONS_MANAGER],
    topics: [
      {
        id: 'ops-policy',
        title: 'Plans & rules',
        body: `
          <p>Configure insurance products in <strong>Policy &amp; Benefits Configuration</strong> — limits, co-pays, deductibles, exclusions, and the statutory toggles (PhilHealth-first, Senior/PWD, VAT exemption). Define routing, escalation, and SLA thresholds in <strong>Workflow Rules &amp; SLA Configuration</strong>.</p>`,
      },
      {
        id: 'ops-ai',
        title: 'AI auto-adjudication',
        body: `
          <p>Set the <strong>AI Auto-Adjudication policy</strong> per line/plan/procedure class — confidence thresholds, peso caps, always-human classes, and a master off switch. As a segment's AI-to-human agreement clears the bar, promote it to <strong>Auto</strong> via Graduated Auto-Approval; a kill-switch reverts it if agreement degrades.</p>`,
      },
      {
        id: 'ops-reports',
        title: 'Notifications & reports',
        body: `
          <p>Manage outbound <strong>notification channels, templates, and delivery rules</strong>, and generate <strong>Operational SLA / queue / turnaround</strong> reports to spot backlogs and breaches before service targets are missed.</p>`,
      },
    ],
  },
  {
    id: 'admin',
    title: 'Administration',
    icon: 'fa fa-user-shield',
    roles: [R.SYSTEM_ADMINISTRATOR],
    topics: [
      {
        id: 'admin-users',
        title: 'User Management',
        body: `
          <p><strong>User Management</strong> is your account console. Create users with a role and temporary password, change a user's <strong>business role</strong> inline, enable/disable access, <strong>reset MFA</strong> enrollment, and delete accounts. Search across name, email, and role.</p>
          <p style="margin-top:8px; font-size:13px; color:#9ca3af;">All account mutations are restricted to the System Administrator role server-side.</p>`,
      },
      {
        id: 'admin-access',
        title: 'Access, MFA & integrations',
        body: `
          <p>Use <strong>Access Control, MFA &amp; Compliance</strong> for role/permission matrices and MFA policy. As administrator you also manage the system <strong>integrations</strong> — communication gateway (email/SMS/contact center), payout service connections, and notification rules — plus the <strong>AI / LLM provider settings</strong>.</p>`,
      },
    ],
  },
  {
    id: 'csr',
    title: 'Customer Service',
    icon: 'fa fa-headset',
    roles: [R.CSR_CALL_CENTER, R.CUSTOMER_SERVICE_REP],
    topics: [
      {
        id: 'csr-desk',
        title: 'Support desk',
        body: `
          <p>At the <strong>Customer Service Desk</strong>, search a member or provider, log an inquiry with a category (LOA status, claim discrepancy, general), and track it through resolution with an SLA countdown. Manage email/SMS/contact-center connectivity from <strong>Contact Center Integrations</strong>.</p>`,
      },
    ],
  },
  {
    id: 'carrier-compliance',
    title: 'Carrier & Compliance',
    icon: 'fa fa-chart-bar',
    roles: [R.INSURANCE_COMPANY_REP, R.COMPLIANCE_OFFICER],
    topics: [
      {
        id: 'carrier-dash',
        title: 'Program dashboard',
        body: `
          <p>Insurance carrier representatives monitor real-time program utilization, claims cash flow, and SLA performance in the <strong>Program Performance Dashboard</strong>, filterable by corporate group and period, with CSV export (no raw clinical detail is exposed).</p>`,
      },
      {
        id: 'compliance-reports',
        title: 'Regulatory reports',
        body: `
          <p>Compliance Officers generate <strong>Regulatory, Audit &amp; Compliance</strong> reports consolidating access logs, overrides, claim-handling exceptions, policy deviations, and turnaround compliance into regulator-ready, traceable extracts.</p>`,
      },
    ],
  },
];

// Effective roles used for help matching (mirrors the nav RBAC).
function helpRolesFor(user) {
  if (isSuperuser(user)) return 'all';
  return effectiveRoles(user);
}

function sectionMatches(section, roles) {
  if (section.roles === 'all') return true;
  if (roles === 'all') return true; // superuser
  return section.roles.some((r) => roles.includes(r));
}

/**
 * Build a dynamic "Your workspace" section listing exactly the pages this user
 * can open (from the nav matrix), grouped as in the menu — the clearest, most
 * personal signal that help is role-aware.
 */
function buildWorkspaceSection(user) {
  const groups = menuData.children
    .map((g) => ({ label: g.label, items: (g.children || []).filter((i) => canAccess(user, i.slug)) }))
    .filter((g) => g.items.length > 0);

  const count = groups.reduce((n, g) => n + g.items.length, 0);
  const groupsHtml = groups
    .map((g) => {
      const items = g.items
        .map((i) => `<li><strong>${i.label}</strong></li>`)
        .join('');
      return `<p style="margin-top:12px;"><strong>${g.label}</strong></p><ul>${items}</ul>`;
    })
    .join('');

  return {
    id: 'your-workspace',
    title: 'Your workspace',
    icon: 'fa fa-compass',
    roles: 'all',
    topics: [
      {
        id: 'your-pages',
        title: 'Your pages',
        body: `
          <p>Your account can open <strong>${count}</strong> page${count === 1 ? '' : 's'}. These are everything your role is permitted to use — and exactly what appears in your navigation.</p>
          ${groupsHtml || '<p style="margin-top:8px;">No workspace pages are provisioned for your role yet — contact your administrator.</p>'}
          <p style="margin-top:12px; font-size:13px; color:#9ca3af;">Tip: trying to open anything else returns a 403. Use <strong>Go to Dashboard</strong> to come back to your home page.</p>`,
      },
    ],
  };
}

/**
 * The ordered list of help sections to show this user:
 * Welcome → Your workspace → role-relevant sections → FAQ.
 */
export function sectionsForUser(user) {
  const roles = helpRolesFor(user);
  const roleSections = SECTIONS.filter((s) => sectionMatches(s, roles));
  return [WELCOME, buildWorkspaceSection(user), ...roleSections, FAQ];
}

// Map the current route to the most relevant help section id (for the given user's sections).
const SLUG_TO_SECTION = [
  [['explanation-of-benefits', 'file-reimbursement', 'request-cashless-loa'], 'member'],
  [['provider-portal', 'provider-claims', 'submit-cashless-loa-walk-in', 'inpatient', 'submit-dental', 'submit-veterinary'], 'provider'],
  [['corporate-hr'], 'corporate'],
  [['claims-inbox', 'claims-adjudication', 'claims-appeal', 'ocr-claim', 'coordination-of-benefits', 'fraud-waste'], 'claims'],
  [['clinical-pre-auth', 'clinical-guidelines', 'inpatient-stay'], 'clinical'],
  [['finance-disbursements', 'member-reimbursement-payout', 'carrier-funds', 'payout-service', 'financial-claims'], 'finance'],
  [['policy-benefits', 'workflow-rules', 'notification-channels', 'operational-sla'], 'operations'],
  [['user-management', 'access-control', 'ai-llm-provider'], 'admin'],
  [['customer-service-desk', 'communication-gateway'], 'csr'],
  [['carrier-program', 'regulatory-audit'], 'carrier-compliance'],
];

export function getDefaultSection(pathname, user) {
  const available = new Set(sectionsForUser(user).map((s) => s.id));
  for (const [needles, sectionId] of SLUG_TO_SECTION) {
    if (needles.some((n) => pathname.includes(n)) && available.has(sectionId)) return sectionId;
  }
  return 'welcome';
}
